import React from 'react';
import CounsellingCard, { CounsellingRecord } from './CounsellingCard';
import EmptyState from '../../UI/EmptyState';
import styles from '../../../styles/CounsellingManagement.module.css';

interface CounsellingListProps {
  counsellings: CounsellingRecord[];
  loading: boolean;
  error: any;
  isInitialLoad: boolean;
  onCounsellingClick: (counselling: CounsellingRecord) => void;
  onRetry: () => void;
  onEdit?: (counselling: CounsellingRecord) => void;
  onDelete?: (counselling: CounsellingRecord) => void;
  onDownload?: (counselling: CounsellingRecord) => void;
  showEditDelete?: boolean | ((counselling: CounsellingRecord) => boolean);
  emptyStateTitle?: string;
  emptyStateDescription?: string;
}

const CounsellingList: React.FC<CounsellingListProps> = ({
  counsellings,
  loading,
  error,
  isInitialLoad,
  onCounsellingClick,
  onRetry,
  onEdit,
  onDelete,
  onDownload,
  showEditDelete = false,
  emptyStateTitle,
  emptyStateDescription
}) => {
  if (isInitialLoad || loading) {
    return (
      <div className={styles.counsellingList}>
        {Array.from({ length: 3 }, (_, index) => (
          <div key={index} className={styles.counsellingCardSkeleton}>
            <div className={styles.skeletonCardHeader}>
              <div className={styles.skeletonCardHeaderLeft}>
                <div className={styles.skeletonIconWrapper}></div>
                <div className={styles.skeletonTitleSection}>
                  <div className={styles.skeletonTitle}></div>
                  <div className={styles.skeletonDescription}></div>
                </div>
              </div>
              <div className={styles.skeletonStatusBadge}></div>
            </div>
            <div className={styles.skeletonCardBody}>
              <div className={styles.skeletonTags}>
                <div className={styles.skeletonTag}></div>
                <div className={styles.skeletonTag}></div>
                <div className={styles.skeletonTag}></div>
              </div>
              <div className={styles.skeletonMetadata}>
                <div className={styles.skeletonMetadataItem}></div>
                <div className={styles.skeletonMetadataItem}></div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.counsellingList}>
        <EmptyState
          icon="⚠️"
          title="Failed to Load Counselling Records"
          description="Unable to load counselling records. Please check your connection and try again."
          action={{
            label: "Retry Loading",
            onClick: onRetry
          }}
        />
      </div>
    );
  }

  if (counsellings.length === 0) {
    return (
      <div className={styles.counsellingList}>
        <EmptyState
          image="/images/notasks.png"
          title={emptyStateTitle || "No Counselling Records Found"}
          description={
            emptyStateDescription ||
            "You haven't created any counselling records yet. Click 'Add Counselling' to get started."
          }
        />
      </div>
    );
  }

  return (
    <div className={styles.counsellingList}>
      {counsellings.map((counselling: CounsellingRecord) => {
        const shouldShowEditDelete = typeof showEditDelete === 'function'
          ? showEditDelete(counselling)
          : showEditDelete;

        return (
          <CounsellingCard
            key={counselling.id}
            counselling={counselling}
            onClick={onCounsellingClick}
            onEdit={onEdit}
            onDelete={onDelete}
            onDownload={onDownload}
            showEditDelete={shouldShowEditDelete}
          />
        );
      })}
    </div>
  );
};

export default CounsellingList;
